import { forwardRef, useContext, useRef } from 'react'
import { useComposedRefs } from '../../../../../utils/customHooks'
import { RadioGroupContext } from './radio-context'
import RadioRoot from './radio-root'
import {
  StyledLabel,
  StyledLabelSkeleton,
  StyledRadioContainer,
  StyledRadioIndicatorSkeleton,
  StyledRadioItemSkeleton
} from '../styled-components'
import PropTypes from 'prop-types'

const RadioItem = forwardRef((props, forwardedRef) => {
  const { label, disabled, customOnCheck, value, ...itemProps } = props
  const ref = useRef(null)
  const composedRefs = useComposedRefs(forwardedRef, ref)

  const groupContext = useContext(RadioGroupContext)

  const isDisabled = groupContext?.disabled || disabled
  const checked = groupContext?.value === value

  if (groupContext?.loading) {
    return (
      <StyledRadioContainer>
        <StyledRadioItemSkeleton size={groupContext?.size}>
          {checked && (
            <StyledRadioIndicatorSkeleton size={groupContext?.size} />
          )}
        </StyledRadioItemSkeleton>
        {label && (
          <StyledLabelSkeleton
            size={groupContext?.size}
            disabled={groupContext?.disabled}
          >
            {label}
          </StyledLabelSkeleton>
        )}
      </StyledRadioContainer>
    )
  }

  return (
    <StyledRadioContainer>
      <RadioRoot
        variant={groupContext?.variant}
        size={groupContext?.size}
        disabled={isDisabled}
        required={groupContext?.required}
        checked={checked}
        value={value}
        {...itemProps}
        name={groupContext?.name}
        ref={composedRefs}
        onCheck={() => {
          groupContext?.onValueChange(value)
          customOnCheck && customOnCheck()
        }}
        onKeyDown={(event) => {
          // According to WAI ARIA, radio groups don't activate items on enter keypress
          if (event.key === 'Enter') event.preventDefault()
        }}
      />
      {label && (
        <StyledLabel
          size={groupContext?.size}
          data-disabled={groupContext?.disabled ? '' : undefined}
          disabled={groupContext?.disabled}
        >
          {label}
        </StyledLabel>
      )}
    </StyledRadioContainer>
  )
})

RadioRoot.propTypes = {
  value: PropTypes.string,
  customOnCheck: PropTypes.func,
  label: PropTypes.string
}

RadioItem.displayName = 'RadioItem'
export default RadioItem
